import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ChefHat, Minus, Plus, Users, Sparkles, X, Share2, Copy, Star, AlertTriangle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { isFavorite, addFavorite, removeFavorite, loadProfile } from "@/lib/mbStorage";
import { getDietaryWarnings } from "@/lib/mbNutrition";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtQty = (q) => {
  const n = Math.round(q * 10) / 10;
  return Number.isInteger(n) ? String(n) : n.toFixed(1).replace(".", ",");
};

const PLURAL = {
  cucchiaio: "cucchiai",
  cucchiaino: "cucchiaini",
  tazza: "tazze",
  fetta: "fette",
  foglia: "foglie",
  pizzico: "pizzichi",
  bicchiere: "bicchieri",
  spicchio: "spicchi",
  gambo: "gambi",
  rametto: "rametti",
  mestolo: "mestoli",
};

const pluralizeUnit = (unit, qty) => {
  const u = (unit || "").trim();
  if (qty === 1 || !u) return u;
  return PLURAL[u.toLowerCase()] || u;
};

const formatIngredient = (ing, servings) => {
  const unit = (ing.unit || "").toLowerCase();
  const isQb = unit.includes("q.b") || ing.quantity <= 0;
  if (isQb) return "q.b.";
  const scaled = ing.quantity * servings;
  return `${fmtQty(scaled)} ${pluralizeUnit(ing.unit, scaled)}`.trim();
};

export const RecipeSheet = ({ meal, onClose, presetRecipe }) => {
  const open = !!meal;
  const [loading, setLoading] = useState(false);
  const [recipe, setRecipe] = useState(null);
  const [servings, setServings] = useState(1);
  const [fav, setFav] = useState(false);

  useEffect(() => {
    if (!meal) return;
    setRecipe(null);
    setServings(1);
    setFav(isFavorite(meal.id));

    if (presetRecipe) {
      setRecipe(presetRecipe);
      setLoading(false);
      return;
    }

    let active = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.post(`${API}/recipe`, {
          dish_name: meal.dish_name,
          ingredients: meal.ingredients || null,
        });
        if (active) setRecipe(data);
      } catch (err) {
        if (process.env.NODE_ENV !== "production") console.error(err);
        toast.error("Ricetta non disponibile", {
          description: err?.response?.data?.detail || "Riprova più tardi",
        });
        if (active) onClose?.();
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meal?.id]);

  const toggleFavorite = () => {
    if (!recipe || !meal) return;
    if (fav) {
      removeFavorite(meal.id);
      setFav(false);
      toast("Rimossa dai preferiti");
    } else {
      addFavorite({
        id: meal.id,
        dish_name: meal.dish_name,
        image: meal.image || null,
        recipe,
      });
      setFav(true);
      toast.success("Salvata tra i preferiti ⭐", { description: recipe.title });
    }
  };

  const buildRecipeText = () => {
    if (!recipe) return "";
    const lines = [];
    lines.push(`🍽️ ${recipe.title}`);
    lines.push(`👥 Per ${servings} ${servings === 1 ? "persona" : "persone"}`);
    lines.push("");
    lines.push("🧾 Ingredienti:");
    recipe.ingredients.forEach((ing) => lines.push(`• ${ing.name}: ${formatIngredient(ing, servings)}`));
    lines.push("");
    lines.push("👩‍🍳 Preparazione:");
    recipe.steps.forEach((s, i) => lines.push(`${i + 1}. ${s}`));
    lines.push("");
    lines.push("Ricetta generata con mangiaBene 🥗");
    return lines.join("\n");
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(buildRecipeText());
      toast.success("Ricetta copiata!", { description: "Ora puoi incollarla dove vuoi 📋" });
    } catch {
      toast.error("Copia non riuscita");
    }
  };

  const handleShare = async () => {
    const text = buildRecipeText();
    if (navigator.share) {
      try {
        await navigator.share({ title: recipe?.title || "Ricetta", text });
        return;
      } catch (e) {
        if (e && e.name === "AbortError") return;
      }
    }
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  const profile = loadProfile();
  const warnings = getDietaryWarnings(profile, [
    meal?.dish_name,
    ...(meal?.ingredients || []),
    recipe?.title,
    ...((recipe?.ingredients || []).map((i) => i.name)),
  ]);

  return (
    <Drawer open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DrawerContent className="max-w-[440px] mx-auto" data-testid="recipe-sheet">
        <div className="px-5 pb-8 pt-2 max-h-[82vh] overflow-y-auto">
          <DrawerHeader className="p-0 mb-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <span className="text-[10px] uppercase tracking-wide text-[#2cd4bf] font-bold flex items-center gap-1">
                  <ChefHat size={13} /> Ricetta Dinamica
                </span>
                <DrawerTitle className="font-display text-xl font-bold text-slate-800 leading-tight" data-testid="recipe-title">
                  {loading ? "Sto cucinando la ricetta..." : recipe?.title || meal?.dish_name}
                </DrawerTitle>
              </div>
              <button
                onClick={toggleFavorite}
                disabled={!recipe}
                data-testid="recipe-favorite-btn"
                aria-label="Salva tra i preferiti"
                className="shrink-0 w-10 h-10 rounded-full bg-slate-50 border border-slate-200 flex items-center justify-center active:scale-90 transition-transform disabled:opacity-40"
              >
                <Star size={20} className={fav ? "fill-[#f59e0b] text-[#f59e0b]" : "text-slate-300"} />
              </button>
            </div>
          </DrawerHeader>

          {warnings.length > 0 && (
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 flex items-start gap-2.5 mb-4" data-testid="recipe-allergy-alert">
              <AlertTriangle size={16} className="text-[#f59e0b] shrink-0 mt-0.5" />
              <div className="text-[12px] leading-relaxed text-[#92400e]">
                <p className="font-bold mb-0.5">Attenzione, controlla il tuo profilo!</p>
                <ul className="space-y-0.5">
                  {warnings.map((w, i) => (
                    <li key={`rwarn-${i}`}>• {w}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {loading && (
            <div className="py-6" data-testid="recipe-loading">
              <div className="text-sm text-[#2b90ef] font-semibold mb-3 text-center flex items-center justify-center gap-2">
                <Sparkles size={14} className="animate-pulse" /> mangiaBene sta scrivendo la ricetta...
              </div>
              <div className="h-4 mb-skeleton rounded-md mb-2.5" />
              <div className="h-4 mb-skeleton rounded-md mb-2.5 w-4/5" />
              <div className="h-4 mb-skeleton rounded-md w-2/3" />
            </div>
          )}

          {!loading && recipe && (
            <>
              {/* Selettore persone */}
              <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-xl p-2.5 mb-4">
                <span className="text-[12px] font-semibold text-slate-600 flex items-center gap-1.5">
                  <Users size={15} className="text-[#2b90ef]" /> Numero di persone
                </span>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setServings((s) => Math.max(1, s - 1))}
                    data-testid="recipe-servings-minus"
                    className="w-8 h-8 rounded-lg bg-white border border-slate-200 text-slate-600 flex items-center justify-center active:scale-90 transition-transform disabled:opacity-40"
                    disabled={servings <= 1}
                  >
                    <Minus size={15} />
                  </button>
                  <span className="text-lg font-bold font-display text-slate-800 w-6 text-center" data-testid="recipe-servings-count">{servings}</span>
                  <button
                    onClick={() => setServings((s) => Math.min(20, s + 1))}
                    data-testid="recipe-servings-plus"
                    className="w-8 h-8 rounded-lg bg-[#2b90ef] text-white flex items-center justify-center active:scale-90 transition-transform"
                  >
                    <Plus size={15} />
                  </button>
                </div>
              </div>

              {/* Ingredienti */}
              <span className="text-[10px] uppercase tracking-wide text-slate-400 font-bold block mb-2">Ingredienti</span>
              <div className="rounded-xl border border-slate-100 overflow-hidden mb-4" data-testid="recipe-ingredients">
                {recipe.ingredients.map((ing, i) => (
                  <div
                    key={`${ing.name}-${i}`}
                    data-testid="recipe-ingredient"
                    className={`flex items-center justify-between px-3 py-2 text-[13px] ${i % 2 ? "bg-slate-50" : "bg-white"}`}
                  >
                    <span className="text-slate-700">{ing.name}</span>
                    <span className="font-semibold text-slate-900 shrink-0 ml-3" data-testid="recipe-ingredient-qty">{formatIngredient(ing, servings)}</span>
                  </div>
                ))}
              </div>

              {/* Passaggi */}
              <span className="text-[10px] uppercase tracking-wide text-slate-400 font-bold block mb-2">Preparazione</span>
              <ol className="space-y-2.5 mb-2" data-testid="recipe-steps">
                {recipe.steps.map((step, i) => (
                  <li key={`step-${i}`} className="flex gap-2.5">
                    <span className="shrink-0 w-6 h-6 rounded-full bg-[#e0f2fe] text-[#2b90ef] font-bold text-[12px] flex items-center justify-center font-display">{i + 1}</span>
                    <p className="text-[13px] text-slate-600 leading-relaxed pt-0.5">{step}</p>
                  </li>
                ))}
              </ol>

              <button
                onClick={toggleFavorite}
                disabled={!recipe}
                data-testid="recipe-save-favorite-btn"
                className={`w-full mt-5 py-3.5 rounded-xl font-bold text-sm active:scale-[0.98] transition-transform flex items-center justify-center gap-2 disabled:opacity-40 ${
                  fav
                    ? "bg-[#f59e0b] text-white"
                    : "bg-[#fff7ed] text-[#f59e0b] border border-[#fcd9a5]"
                }`}
              >
                <Star size={18} className={fav ? "fill-white" : "fill-[#f59e0b]"} />
                {fav ? "Salvata nei Preferiti" : "Salva nei Preferiti"}
              </button>

              <div className="flex gap-2 mt-2">
                <button
                  onClick={handleShare}
                  data-testid="recipe-share-btn"
                  className="flex-1 py-3 bg-[#25D366] hover:bg-[#1ebe5a] text-white rounded-xl font-semibold text-sm active:scale-[0.98] transition-transform flex items-center justify-center gap-2"
                >
                  <Share2 size={16} /> Condividi
                </button>
                <button
                  onClick={handleCopy}
                  data-testid="recipe-copy-btn"
                  className="px-4 py-3 bg-[#e0f2fe] hover:bg-[#c7e7fb] text-[#2b90ef] rounded-xl font-semibold text-sm active:scale-[0.98] transition-transform flex items-center justify-center gap-2"
                >
                  <Copy size={16} /> Copia
                </button>
              </div>

              <button
                onClick={onClose}
                data-testid="recipe-close-btn"
                className="w-full mt-2 py-3 bg-slate-100 text-slate-600 rounded-xl font-semibold text-sm active:scale-[0.98] transition-transform flex items-center justify-center gap-1.5"
              >
                <X size={15} /> Chiudi
              </button>
            </>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
};
